import React from 'react';
import { AlertTriangle, Pencil } from 'lucide-react';
import type { ComponentWithReplacement } from '../../types';
import type { Building } from '../../../../../types/protocols';
import { getBuildComponentTypeTranslation } from '../../../../../Helpers/BuildComponentTypesStore';
import { MIXED_VALUE } from '../../utils/bulkSelection';

interface BulkSelectionHeaderProps {
    plans: ComponentWithReplacement[];
    building: Building | null;
    /** The selected components without skick or livslängd */
    incompletePlans: ComponentWithReplacement[];
    /** Today's condition shared by every selected component, null when they differ or none is assessed */
    commonCondition: number | null;
    /** How many more components of the type the list has */
    unselectedOfType: number;
    onSelectAllOfType: () => void;
    onClearSelection: () => void;
    onOpenConditionModal: () => void;
    disabled: boolean;
}

/**
 * The head of the Massredigering card: which components are being edited,
 * their shared skick, and the selection actions. Replaces ComponentHeader,
 * whose name, Mängd and images are per component.
 */
export const BulkSelectionHeader: React.FC<BulkSelectionHeaderProps> = ({
    plans,
    building,
    incompletePlans,
    commonCondition,
    unselectedOfType,
    onSelectAllOfType,
    onClearSelection,
    onOpenConditionModal,
    disabled
}) => {
    const first = plans[0].component;
    const typeName = first.specificType?.value
        ? getBuildComponentTypeTranslation(first.specificType.value, first.type)
        : '';

    return (
        <div className="component-header-row bulk-selection-header">
            <div className="component-title">
                <h6 className="component-name">{plans.length} komponenter · {typeName}</h6>
                <div className="bulk-selection-names">
                    {plans.map(plan => (
                        <span key={plan.component.key} className="bulk-selection-name">
                            {building ? plan.component.displayName(building) : plan.component.key}
                        </span>
                    ))}
                </div>
                <div className="bulk-selection-actions">
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onSelectAllOfType}
                        disabled={unselectedOfType === 0}
                    >
                        Välj alla {plans.length + unselectedOfType} av typen
                    </button>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={onClearSelection}>
                        Rensa val
                    </button>
                </div>
            </div>

            <div className="component-metadata-section">
                <div className="metadata-item">
                    <span className="metadata-label">Skick</span>
                    <div className="metadata-value">
                        <span className={disabled || commonCondition === null ? 'text-muted' : ''}>
                            {commonCondition === null ? MIXED_VALUE : `${commonCondition.toFixed(1)}/10`}
                        </span>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled) onOpenConditionModal();
                            }}
                            disabled={disabled}
                            title="Ändra skick på alla valda"
                            style={{ lineHeight: 1 }}
                        >
                            <Pencil size={12} className={disabled ? 'text-muted' : 'text-primary'} />
                        </button>
                    </div>
                </div>
            </div>

            {incompletePlans.length > 0 && (
                <div className="component-comment bulk-selection-incomplete">
                    <AlertTriangle size={14} className="text-warning" aria-hidden="true" />
                    <small>
                        {incompletePlans.length} av {plans.length} saknar skick eller livslängd:{' '}
                        {incompletePlans.map(p => (building ? p.component.displayName(building) : p.component.key)).join(', ')}.
                        Aktiviteter kan redigeras när alla valda har skick och livslängd.
                    </small>
                </div>
            )}
        </div>
    );
};
